/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, PageGridContainer, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { AddCloudDesktopIcon, BriefcaseIcon, DownloadIcon, KeyboardIcon, StarIcon } from '@coursera/cds-icons';

import CloudDownloadIcon from 'bundles/unified-description-page-common/components/modules/project-instructions/CloudDownloadIcon';
import TeacherIcon from 'bundles/unified-description-page-common/components/modules/project-instructions/TeacherIcon';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import type { ProjectInstruction } from 'bundles/unified-description-page-common/data/project-instructions';

type Props = {
  instructions?: ProjectInstruction[];
  backgroundImage: string;
  title: string;
  anchorId: string;
};

const iconsToUse = {
  AddCloudDesktopIcon,
  CloudDownloadIcon,
  DownloadIcon,
  TeacherIcon,
  KeyboardIcon,
  StarIcon,
  BriefcaseIcon,
};

const styles = {
  container: (backgroundImage: string) => css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-400) var(--cds-spacing-400);

    ${breakpoints.up('md')} {
      background: url(${backgroundImage}) top right no-repeat;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-200);
    }
  `,
  instructions: css`
    padding-top: var(--cds-spacing-300);
    margin-bottom: 0;

    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-200);
    }
  `,
  instruction: css`
    padding: var(--cds-spacing-300) 0 0;
    display: flex;
    gap: var(--cds-spacing-200);

    svg:not(:root) {
      overflow: visible;
    }
  `,
  icon: css`
    flex-shrink: 0;
  `,
};

const ProjectInstructionsModule = ({ instructions, backgroundImage, title, anchorId }: Props) => {
  if (!instructions || !instructions.length) {
    return null;
  }

  return (
    <Section anchorId={anchorId}>
      <PageGridContainer>
        <Grid item xs={12}>
          <div css={styles.container(backgroundImage)}>
            <Grid container>
              <Grid item xs={12} md={6}>
                <Typography variant="h1semibold" component="h2">
                  {title}
                </Typography>
                <ul css={styles.instructions}>
                  {instructions.map((instruction) => {
                    const Icon = iconsToUse[instruction.icon as keyof typeof iconsToUse];

                    return (
                      <li key={instruction.title} css={styles.instruction}>
                        {Icon && <Icon size="large" css={styles.icon} />}
                        <div>
                          <Typography2 variant="subtitleMedium" component="p">
                            {instruction.title}
                          </Typography2>
                          <Typography2 component="p" variant="bodyPrimary">
                            {instruction.description}
                          </Typography2>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </PageGridContainer>
    </Section>
  );
};

export default ProjectInstructionsModule;
